import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  useMediaQuery,
  alpha,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Typography
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

// Internal components
import StepIndicator from './StepIndicator';
import NavigationControls from './NavigationControls';
import FormSectionContainer from './FormSectionContainer';

// Hooks
import useStepNavigation from '../../../../hooks/useStepNavigation';
import useAutoSave from '../../../../hooks/useAutoSave';
import useForm from '../../../../hooks/useForm';

// Styled components
const FormContainer = styled(Paper)(({ theme, magical }) => ({
  padding: theme.spacing(4),
  borderRadius: magical ? 16 : 8,
  position: 'relative',
  background: theme.palette.background.paper,
  boxShadow: magical
    ? theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(123, 104, 238, 0.08)'
      : `0 8px 32px rgba(0, 0, 0, 0.08), 0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`
    : theme.shadows[2],
  
  [theme.breakpoints?.down('md') || '(max-width: 960px)']: {
    padding: theme.spacing(3),
  },
  
  [theme.breakpoints?.down('sm') || '(max-width: 600px)']: {
    padding: theme.spacing(2),
    borderRadius: magical ? 12 : 4,
  }
}));

const StepContentContainer = styled(Box)(({ theme }) => ({
  minHeight: 400,
  padding: theme.spacing(2, 0),
  position: 'relative',
  
  // Smooth transitions between steps
  '& .step-content': {
    opacity: 1,
    transform: 'translateX(0)',
    transition: 'opacity 0.3s ease, transform 0.3s ease',
  },
  
  '& .step-content.entering': {
    opacity: 0,
    transform: 'translateX(20px)',
  },
  
  '& .step-content.exiting': {
    opacity: 0,
    transform: 'translateX(-20px)',
  }
}));

/**
 * Multi-step form component with comprehensive features
 * 
 * This component provides a complete multi-step form solution with:
 * - Step navigation and validation
 * - Auto-save and draft management
 * - Progress persistence
 * - Responsive design
 * - Accessibility support
 * - Consistent UX patterns
 * 
 * @param {Object} props - Component properties
 * @param {Array} props.steps - Array of step configurations
 * @param {Object} props.initialValues - Initial form values
 * @param {Function} props.onSubmit - Form submission handler
 * @param {Function} props.onStepChange - Step change callback
 * @param {Function} props.onCancel - Cancel callback
 * @param {string} props.cancelLink - Cancel navigation link
 * @param {Function} props.validateStep - Step validation function
 * @param {Function} props.saveAsDraft - Draft save function
 * @param {string} props.formKey - Unique key for form persistence
 * @param {boolean} props.autoSave - Enable auto-save functionality
 * @param {number} props.autoSaveDelay - Auto-save delay in milliseconds
 * @param {boolean} props.linear - Linear step navigation
 * @param {boolean} props.magical - Enhanced visual styling
 * @param {string} props.title - Form title
 * @param {string} props.description - Form description
 * @param {boolean} props.showProgress - Show progress indicator
 * @param {boolean} props.showSaveStatus - Show save status
 * @param {boolean} props.responsive - Responsive design
 * @param {string} props.variant - Display variant
 * @param {Object} props.navigationLabels - Custom navigation labels
 * @param {Object} props.sx - Additional styles
 */
const MultiStepForm = ({
  steps = [],
  initialValues = {},
  onSubmit,
  onStepChange,
  onCancel,
  cancelLink,
  validateStep,
  saveAsDraft,
  formKey,
  autoSave = true,
  autoSaveDelay = 2000,
  linear = true,
  magical = false,
  title,
  description,
  showProgress = true,
  showSaveStatus = true,
  responsive = true,
  variant = 'default',
  navigationLabels = {},
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints?.down('md') || '(max-width: 960px)');
  
  // State
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form management
  const {
    formData,
    validationErrors,
    touchedFields,
    handleChange,
    handleNestedChange,
    handleDeepNestedChange,
    handleArrayChange,
    validateField,
    validateStep: validateFormStep,
    updateFormData,
    resetForm
  } = useForm(initialValues);
  
  // Step navigation
  const {
    currentStep,
    completedSteps,
    visitedSteps,
    validationErrors: stepErrors,
    isValidating,
    goToStep,
    nextStep,
    previousStep,
    reset: resetNavigation,
    getProgress,
    getStepStatus,
    isLastStep,
    isFirstStep
  } = useStepNavigation({
    steps,
    initialStep: 0,
    persistenceKey: formKey,
    validateStep: useCallback(async (stepIndex) => {
      // Use custom validation if provided
      if (validateStep) {
        return await validateStep(stepIndex, formData);
      }
      
      // Use form validation for current step
      const currentStepConfig = steps[stepIndex];
      if (currentStepConfig?.validation) {
        const result = validateFormStep(currentStepConfig.validation);
        return result.isValid;
      }
      
      return true;
    }, [validateStep, formData, validateFormStep, steps]),
    onStepChange,
    onComplete: useCallback(async () => {
      if (onSubmit) {
        setIsSubmitting(true);
        try {
          await onSubmit(formData);
        } finally {
          setIsSubmitting(false);
        }
      }
    }, [onSubmit, formData]),
    linear
  });
  
  // Auto-save functionality
  const autoSaveConfig = useMemo(() => ({
    delay: autoSaveDelay,
    key: formKey,
    enabled: autoSave && !!saveAsDraft,
    onSaved: (data) => {
      console.log('Auto-saved form data:', data);
    },
    onError: (error) => {
      console.error('Auto-save failed:', error);
    }
  }), [autoSaveDelay, formKey, autoSave, saveAsDraft]);
  
  const {
    saveStatus,
    lastSaved,
    hasUnsavedChanges,
    saveNow,
    loadDraft,
    clearDraft,
    getDraftMetadata
  } = useAutoSave(
    formData,
    saveAsDraft,
    autoSaveConfig
  );
  
  // Load draft on mount
  useEffect(() => {
    if (formKey) {
      const draft = loadDraft();
      if (draft) {
        const metadata = getDraftMetadata();
        
        // Ask user if they want to restore draft
        if (metadata && Date.now() - metadata.timestamp < 24 * 60 * 60 * 1000) { // 24 hours
          // For now, auto-restore. Could show a dialog instead.
          updateFormData(draft);
        }
      }
    }
  }, [formKey, loadDraft, getDraftMetadata, updateFormData]);
  
  /**
   * Handles step navigation
   */
  const handleStepNavigation = useCallback(async (direction) => {
    if (direction === 'next') {
      return await nextStep();
    } else if (direction === 'previous') {
      return await previousStep();
    }
    return false;
  }, [nextStep, previousStep]);
  
  /**
   * Handles form submission
   */
  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    
    try {
      if (onSubmit) {
        await onSubmit(formData);
        
        // Clear draft on successful submission
        if (formKey) {
          clearDraft();
        }
      }
    } catch (error) {
      console.error('Form submission failed:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit, formData, formKey, clearDraft]);
  
  /**
   * Handles save draft
   */
  const handleSaveDraft = useCallback(async () => {
    if (saveAsDraft) {
      try {
        await saveAsDraft(formData);
      } catch (error) {
        console.error('Save draft failed:', error);
      }
    } else {
      // Just save to localStorage
      await saveNow();
    }
  }, [saveAsDraft, formData, saveNow]);
  
  /**
   * Handles cancel with unsaved changes check
   */
  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowExitDialog(true);
    } else {
      if (onCancel) {
        onCancel();
      }
    }
  }, [hasUnsavedChanges, onCancel]);
  
  /**
   * Confirms exit without saving
   */
  const handleConfirmExit = useCallback(() => {
    setShowExitDialog(false);
    if (formKey) {
      clearDraft();
    }
    if (onCancel) {
      onCancel();
    }
  }, [formKey, clearDraft, onCancel]);
  
  /**
   * Gets current step content
   */
  const getCurrentStepContent = useCallback(() => {
    const step = steps[currentStep];
    if (!step) return null;
    
    // Pass form props to step component
    const stepProps = {
      formData,
      errors: validationErrors,
      touched: touchedFields,
      onChange: handleChange,
      onNestedChange: handleNestedChange,
      onDeepNestedChange: handleDeepNestedChange,
      onArrayChange: handleArrayChange,
      validateField,
      currentStep,
      isActive: true
    };
    
    // If step has a render function, use it
    if (typeof step.component === 'function') {
      return step.component(stepProps);
    }
    
    // If step component is a React element, clone with props
    if (React.isValidElement(step.component)) {
      return React.cloneElement(step.component, stepProps);
    }
    
    return step.component;
  }, [
    steps,
    currentStep,
    formData,
    validationErrors,
    touchedFields,
    handleChange,
    handleNestedChange,
    handleDeepNestedChange,
    handleArrayChange,
    validateField
  ]);
  
  // Progress information
  const progress = getProgress();
  
  // Current step configuration
  const currentStepConfig = steps[currentStep] || {};
  const stepStatus = getStepStatus(currentStep);
  
  // Navigation labels with defaults
  const navLabels = {
    next: 'Next',
    previous: 'Back',
    submit: 'Submit',
    cancel: 'Cancel',
    saveDraft: 'Save Draft',
    ...navigationLabels
  };
  
  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        py: responsive ? { xs: 2, md: 4 } : 4,
        ...sx 
      }}
      {...props}
    >
      <FormContainer magical={magical}>
        {/* Form header */}
        {(title || description) && (
          <Box sx={{ mb: 4 }}>
            {title && (
              <Typography variant="h4" component="h1" gutterBottom>
                {title}
              </Typography>
            )}
            {description && (
              <Typography variant="body1" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>
        )}
        
        {/* Step indicator */}
        <StepIndicator
          steps={steps}
          currentStep={currentStep}
          completedSteps={completedSteps}
          stepErrors={stepErrors}
          onStepClick={goToStep}
          linear={linear}
          showProgress={showProgress}
          variant={responsive && isMobile ? 'compact' : 'horizontal'}
        />
        
        {/* Step content */}
        <StepContentContainer>
          <Box className="step-content">
            {currentStepConfig.section ? (
              <FormSectionContainer
                title={currentStepConfig.label}
                description={currentStepConfig.description}
                icon={currentStepConfig.icon}
                required={!currentStepConfig.optional}
                optional={currentStepConfig.optional}
                errors={stepErrors[currentStep] ? Object.values(stepErrors[currentStep]) : []}
                isCompleted={completedSteps.includes(currentStep)}
                helpText={currentStepConfig.helpText}
                helpContent={currentStepConfig.helpContent}
              >
                {getCurrentStepContent()}
              </FormSectionContainer>
            ) : (
              getCurrentStepContent()
            )}
          </Box>
        </StepContentContainer>
        
        {/* Navigation controls */}
        <NavigationControls
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          onPrevious={() => handleStepNavigation('previous')}
          onNext={() => handleStepNavigation('next')}
          onSubmit={handleSubmit}
          onSaveDraft={handleSaveDraft}
          onCancel={cancelLink ? undefined : handleCancel}
          cancelLink={cancelLink}
          isSubmitting={isSubmitting}
          isValidating={isValidating}
          canProceed={!stepStatus.hasErrors}
          nextButtonText={navLabels.next}
          previousButtonText={navLabels.previous}
          submitButtonText={navLabels.submit}
          saveDraftText={navLabels.saveDraft}
          cancelText={navLabels.cancel}
          saveStatus={{
            saveStatus,
            lastSaved,
            hasUnsavedChanges,
            isSaving: saveStatus === 'saving'
          }}
          showSaveStatus={showSaveStatus && autoSave}
          showDraftButton={!!saveAsDraft}
          linear={linear}
        />
      </FormContainer>
      
      {/* Exit confirmation dialog */}
      <Dialog
        open={showExitDialog}
        onClose={() => setShowExitDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to leave without saving?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExitDialog(false)}>
            Stay
          </Button>
          <Button onClick={handleSaveDraft} variant="outlined">
            Save Draft
          </Button>
          <Button onClick={handleConfirmExit} color="error">
            Leave Without Saving
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MultiStepForm;